/**
 * AI Blueprint library — main backend `/api/v1/ai-blueprints`.
 */

import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'

export interface ApqcProcessRef {
  id: string
  name: string
  path?: string | null
}

export interface AiBlueprintGuidelines {
  must?: string[]
  must_not?: string[]
  notes?: string
}

export type BlueprintType = 'agent' | 'workflow'
/** Values accepted on tenant create. System packs use ``system`` on read only. */
export type TenantSeedSource = 'apqc' | 'research_paper' | 'custom_input'
export type SeedSource = TenantSeedSource | 'system'

export function isSystemBlueprint(bp: { is_system?: boolean; id?: string; tags?: string[] | null }): boolean {
  if (bp.is_system) return true
  if (bp.id?.startsWith('system-bp-')) return true
  return Boolean(bp.tags?.includes('system'))
}

export interface AiBlueprintVersionSummary {
  semver: string
  blueprint_type?: BlueprintType
  seed_source?: SeedSource
  status: string
  tokens_input: number
  tokens_output: number
  foundation_model_id?: string | null
  model_id?: string | null
  estimated_cost_usd?: number | null
  created_at: number
  created_by?: string | null
  created_by_label?: string | null
  approved_at?: number | null
  approved_by?: string | null
  approved_by_label?: string | null
}

export interface AiBlueprintVersionRead {
  semver: string
  blueprint_type?: BlueprintType
  seed_source?: SeedSource
  name: string
  description?: string | null
  icon_key: string
  icon_file_id?: string | null
  industry: string
  apqc_processes: Array<Record<string, unknown>>
  spec: Record<string, unknown> | null
  status: string
  tokens_input: number
  tokens_output: number
  foundation_model_id?: string | null
  model_id?: string | null
  estimated_cost_usd?: number | null
  created_at: number
  created_by?: string | null
  created_by_label?: string | null
  approved_at?: number | null
  approved_by?: string | null
  approved_by_label?: string | null
}

export interface LinkedKpi {
  id: string
  kpi_name: string
  kpi_type?: string | null
  definition?: string | null
  description?: string | null
  family_id?: string | null
  family_name?: string | null
  domain?: string | null
  data_type?: string | null
}

export interface LinkedDataModel {
  id: string
  name: string
  description?: string | null
  industry?: string | null
  version?: string | null
  status?: string | null
  entity_count?: number | null
  relationship_count?: number | null
}

export interface AiBlueprintRead {
  id: string
  name: string
  description?: string | null
  icon_key: string
  icon_file_id?: string | null
  industry: string
  blueprint_type?: BlueprintType
  seed_source?: SeedSource
  /** Platform packs include "system"; tenant blueprints are []. */
  tags?: string[]
  /** True for code-loaded system blueprints (read-only). */
  is_system?: boolean
  apqc_processes: Array<Record<string, unknown>>
  spec: Record<string, unknown> | null
  /** Server-resolved KPI objects linked to this blueprint (read-only). */
  linked_kpis?: LinkedKpi[] | null
  /** Server-resolved unified data model objects linked to this blueprint (read-only). */
  linked_unified_data_models?: LinkedDataModel[] | null
  /** Server-resolved ML solutions linked to this blueprint (read-only). */
  linked_ml_solutions?: Array<Record<string, unknown>> | null
  /** 1–12 = DIP / landing spotlight slot; omitted or null = not featured */
  spotlight_rank?: number | null
  /** Per-user favourite flag resolved server-side on list/detail responses. */
  is_fav?: boolean
  status: string
  version: number
  current_semver: string
  lock_version: number
  created_by?: string | null
  created_by_label?: string | null
  updated_by?: string | null
  updated_by_label?: string | null
  created_at: number
  updated_at: number
}

export interface AiBlueprintCreatePayload {
  name: string
  description?: string | null
  icon_key?: string
  icon_file_id?: string | null
  industry?: string
  blueprint_type: BlueprintType
  seed_source?: TenantSeedSource
  apqc_processes?: ApqcProcessRef[]
  spec?: Record<string, unknown> | null
  status?: 'draft' | 'pending_review' | 'approved' | 'archived'
  enhancement_tokens_input?: number
  enhancement_tokens_output?: number
  enhancement_foundation_model_id?: string
  enhancement_model_id?: string
  enhancement_estimated_cost_usd?: number
  linked_kpis?: LinkedKpi[] | null
  linked_unified_data_models?: LinkedDataModel[] | null
  linked_ml_solutions?: Array<Record<string, unknown>> | null
}

export interface AiBlueprintUpdatePayload {
  name?: string | null
  description?: string | null
  icon_key?: string | null
  icon_file_id?: string | null
  industry?: string | null
  apqc_processes?: ApqcProcessRef[] | null
  spec?: Record<string, unknown> | null
  status?: 'draft' | 'pending_review' | 'approved' | 'archived' | null
  /** Set 1–12 to feature on DIP landing; null removes from spotlight */
  spotlight_rank?: number | null
  lock_version: number
  /** When false, persist draft without bumping semver (use true on explicit Save). Default true if omitted. */
  commit_version?: boolean
  enhancement_tokens_input?: number
  enhancement_tokens_output?: number
  enhancement_foundation_model_id?: string
  enhancement_model_id?: string
  enhancement_estimated_cost_usd?: number
  linked_kpis?: LinkedKpi[] | null
  linked_unified_data_models?: LinkedDataModel[] | null
  linked_ml_solutions?: Array<Record<string, unknown>> | null
}

export interface AiBlueprintListParams {
  page?: number
  page_size?: number
  status?: string
  include_archived?: boolean
  /** Case-insensitive search on name and description (server-side). */
  search?: string
  /** Filter by creator user ID (server-side). */
  created_by?: string
  /** Filter by blueprint type: 'agent' or 'workflow' (server-side). */
  blueprint_type?: BlueprintType
  /** Filter by industry id, e.g. healthcare | retail (server-side). */
  industry?: string
  /** When true, return only the current user's favourite blueprints (server-side). */
  is_favourite?: boolean
  /** When true, annotate every item with is_fav even when is_favourite is false. */
  include_is_fav?: boolean
  /** library (default): tenant blueprints only; system: platform packs only. */
  system_scope?: 'library' | 'system'
  signal?: AbortSignal
}

export const aiBlueprintsApi = {
  list: (params?: AiBlueprintListParams) => {
    const { signal, ...q } = params || {}
    const query = buildQueryString(q)
    return request<{ items: AiBlueprintRead[]; total: number }>(`/ai-blueprints${query}`, { signal })
  },

  get: (
    id: string,
    options?: { signal?: AbortSignal; /** default true — set false to skip large EO payload */ includeUnifiedDataModels?: boolean },
  ) => {
    const q =
      options?.includeUnifiedDataModels === false ? '?include_unified_data_models=false' : ''
    return request<AiBlueprintRead>(`/ai-blueprints/${id}${q}`, { signal: options?.signal })
  },

  listVersions: (blueprintId: string, params?: { page?: number; page_size?: number; signal?: AbortSignal }) => {
    const { signal, ...q } = params || {}
    const query = buildQueryString(q)
    return request<{ items: AiBlueprintVersionSummary[]; total: number }>(
      `/ai-blueprints/${blueprintId}/versions${query}`,
      { signal },
    )
  },

  getVersion: (
    blueprintId: string,
    semver: string,
    options?: { signal?: AbortSignal; includeUnifiedDataModels?: boolean },
  ) => {
    const q =
      options?.includeUnifiedDataModels === false ? '?include_unified_data_models=false' : ''
    return request<AiBlueprintVersionRead>(
      `/ai-blueprints/${blueprintId}/versions/${encodeURIComponent(semver)}${q}`,
      { signal: options?.signal },
    )
  },

  create: (body: AiBlueprintCreatePayload) =>
    request<AiBlueprintRead>('/ai-blueprints', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (id: string, body: AiBlueprintUpdatePayload) =>
    request<AiBlueprintRead>(`/ai-blueprints/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: (id: string) =>
    request<void>(`/ai-blueprints/${id}`, {
      method: 'DELETE',
    }),

  /** APQC process trees from core (no Enterprise Ontology dependency). */
  listApqcProcesses: (params?: {
    domain?: string
    level?: number
    parent_id?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...q } = params || {}
    const query = buildQueryString(q)
    return request<{ items: CatalogApqcProcess[]; total: number }>(
      `/ai-blueprints/catalog/apqc/processes${query}`,
      { signal },
    )
  },

  /** KPI definitions — optional process_ids fetches EO process KPIs via core proxy (empty when EO is down). */
  listCatalogKpis: (params?: {
    domain?: string
    collection_id?: string
    process_ids?: string[]
    signal?: AbortSignal
  }) => {
    const { signal, process_ids, ...rest } = params || {}
    const q: Record<string, string | number | boolean | undefined> = { ...rest }
    if (process_ids?.length) q.process_ids = process_ids.join(',')
    const query = buildQueryString(q)
    return request<{ items: CatalogKpiDefinition[]; total: number }>(
      `/ai-blueprints/catalog/kpis${query}`,
      { signal },
    )
  },
}

export interface CatalogApqcProcess {
  id: string
  name: string
  path?: string | null
  level?: number | null
  parent_id?: string | null
  domain?: string | null
  apqc_element_id?: number | null
}

export interface CatalogKpiDefinition {
  id: string
  kpi_name: string
  kpi_type?: string | null
  definition?: string | null
  description?: string | null
  family_id?: string | null
  [key: string]: unknown
}
